import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@bymason-private/auth-provider';
import { Box, Divider } from '@bymason-private/legacy-pattern-components';
import { Route } from '@bymason-private/route-provider';
import { useGetUsageByDay, useGetUsageBySim } from '@bymason/controller-api-hooks';
import { useToast } from '../../';
import BreakdownHeader from './breakdown-header';
import ConnectivityGridContainer from './grid-container';
import MonthYearSelector from './month-year-selector';
import TotalUsageHeader from './total-usage-header';
import UsageChart from './usage-chart';
import UsageSummaryCard from './usage-summary-card';

interface ConnectivityViewProps {
  iccid?: string;
  simConnectivityRoute: Route;
  isScrolledToBottom?: boolean;
  onMonthChange?: (month: Date) => void;
  onDataLoading?: (loaded: boolean) => void;
  EmptyStateWidget?: React.ElementType;
}

export function ConnectivityView({
  iccid,
  onDataLoading,
  onMonthChange,
  simConnectivityRoute,
  isScrolledToBottom,
  EmptyStateWidget,
}: ConnectivityViewProps) {
  const { authToken = '' } = useAuth();
  const [setToastMessage, setToastSeverity] = useToast();

  // TODO: remove when sim counts added to global context
  const [getHasSims, hasSims, , hasSimsBusy] = useGetUsageBySim(authToken);
  const [getUsageByDay, usageByDay, usageByDayError, usageByDayBusy] = useGetUsageByDay(authToken);
  const [reportMonth, setReportMonth] = useState<Date | null>(null);
  const [bySimError, setBySimError] = useState<string | null>(null);

  const handleGenerateReportError = useCallback(
    (error: string) => {
      setToastMessage(error);
      setToastSeverity('error');
    },
    [setToastMessage, setToastSeverity]
  );

  const handleGenerateReportSuccess = useCallback(
    (email: string) => {
      setToastMessage(`Your report has been successfully sent to ${email}`);
      setToastSeverity('success');
    },
    [setToastMessage, setToastSeverity]
  );

  const handleMonthChange = useCallback(
    (newMonth: Date) => {
      setReportMonth(newMonth);
      onMonthChange && onMonthChange(newMonth);
    },
    [onMonthChange]
  );

  const handleGridContainerErrorChange = useCallback((error: string | null) => {
    setBySimError(error);
  }, []);

  //
  // handle by-day data retrieval
  //
  useEffect(() => {
    if (reportMonth) {
      getUsageByDay({ date: reportMonth, iccid });
    }
  }, [getUsageByDay, reportMonth, iccid]);

  useEffect(() => {
    getHasSims({
      limit: 5,
      month: new Date(),
    });
  }, [getHasSims]);

  //
  // handle ByDay API error
  //
  useEffect(() => {
    if (usageByDayError) {
      setToastMessage(usageByDayError);
      setToastSeverity('error');
    }
  }, [usageByDayError, setToastMessage, setToastSeverity]);

  //
  // handle BySim error
  //
  useEffect(() => {
    if (bySimError) {
      setToastMessage(bySimError);
      setToastSeverity('error');
    }
  }, [bySimError, setToastMessage, setToastSeverity]);

  useEffect(() => {
    if (onDataLoading && hasSimsBusy) {
      onDataLoading(hasSimsBusy);
    }
  }, [onDataLoading, hasSimsBusy]);

  return (
    <Box
      sx={(theme) => ({
        margin: theme.spacing(1.5, 0),
      })}
    >
      {EmptyStateWidget && reportMonth && !usageByDay?.length && !hasSims?.data.length ? (
        !usageByDayBusy && !hasSimsBusy && <EmptyStateWidget />
      ) : (
        <>
          <Box
            sx={{
              alignItems: 'center',
              cursor: 'default',
              display: 'flex',
              justifyContent: 'space-between',
              paddingBottom: 1.5,
            }}
          >
            <TotalUsageHeader />
            <MonthYearSelector onMonthChange={handleMonthChange} />
          </Box>
          <Box
            sx={(theme) => ({
              display: 'flex',
              flexDirection: 'row',
              gap: 2,
              [theme.breakpoints.down(1140)]: {
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              },
            })}
          >
            <UsageSummaryCard usageByDay={usageByDay || []} />
            <UsageChart reportMonth={reportMonth} usageByDay={usageByDay} iccid={iccid} />
          </Box>
          <Divider
            sx={(theme) => ({
              margin: theme.spacing(3, 0, 2),
            })}
          />
          <BreakdownHeader
            iccid={iccid}
            onError={handleGenerateReportError}
            onSuccess={handleGenerateReportSuccess}
          />
          <ConnectivityGridContainer
            iccid={iccid}
            isScrolledToBottom={isScrolledToBottom}
            onError={handleGridContainerErrorChange}
            reportMonth={reportMonth}
            usageByDay={usageByDay}
            simConnectivityRoute={simConnectivityRoute}
          />
        </>
      )}
    </Box>
  );
}
